import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCoaching } from '../../contexts/CoachingContext';
import toast from 'react-hot-toast';

const AXES = ['Marché', 'Production', 'Ressources Humaines', 'Financement', 'Comptabilité', 'Rentabilité', 'Recherche & Développement'];
const MONTHS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
const STATUS_LABELS = { planned: 'Prévu', in_progress: 'En cours', done: 'Réalisé', cancelled: 'Annulé' };
const STATUS_COLORS = {
  planned: 'bg-gray-100 text-gray-700',
  in_progress: 'bg-blue-100 text-blue-700',
  done: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700'
};

const IconPlus = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
  </svg>
);

const IconEdit = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const IconTrash = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const IconDownload = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);

const IconChevron = ({ open }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);

const emptyAction = {
  axis: AXES[0],
  objective: '',
  activity: '',
  technical_resources: '',
  own_financial_resources: '',
  structure_financial_resources: '',
  external_financial_resources: '',
  human_resources: '',
  responsible: '',
  support_persons: '',
  start_month: '',
  end_month: '',
  implementation_date: '',
  status: 'planned'
};

const ActionForm = ({ initial, onSave, onCancel, fixedAxis }) => {
  const [form, setForm] = useState(initial || { ...emptyAction, axis: fixedAxis || AXES[0] });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
      {!fixedAxis && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Axe</label>
          <select value={form.axis} onChange={e => set('axis', e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30">
            {AXES.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Objectif</label>
          <textarea rows={2} value={form.objective} onChange={e => set('objective', e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="Objectif à atteindre..." />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Activité à mener</label>
          <textarea rows={2} value={form.activity} onChange={e => set('activity', e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="Actions concrètes..." />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Ressources techniques</label>
        <input type="text" value={form.technical_resources} onChange={e => set('technical_resources', e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="Matériel, logiciels, équipements..." />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Ressources financières propres (FCFA)</label>
          <input type="number" min="0" value={form.own_financial_resources} onChange={e => set('own_financial_resources', e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="0" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Ressources structure d'accompagnement (FCFA)</label>
          <input type="number" min="0" value={form.structure_financial_resources} onChange={e => set('structure_financial_resources', e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="0" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Autres ressources externes (FCFA)</label>
          <input type="number" min="0" value={form.external_financial_resources} onChange={e => set('external_financial_resources', e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="0" />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Ressources humaines</label>
          <input type="text" value={form.human_resources} onChange={e => set('human_resources', e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="Profils requis..." />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Responsable mise en oeuvre</label>
          <input type="text" value={form.responsible} onChange={e => set('responsible', e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="Nom du responsable..." />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Personnes en appui</label>
        <input type="text" value={form.support_persons} onChange={e => set('support_persons', e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="Conseillers, partenaires..." />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Mois début</label>
          <select value={form.start_month} onChange={e => set('start_month', e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30">
            <option value="">--</option>
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Mois fin</label>
          <select value={form.end_month} onChange={e => set('end_month', e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30">
            <option value="">--</option>
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Date précise</label>
          <input type="date" value={form.implementation_date} onChange={e => set('implementation_date', e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Statut</label>
          <select value={form.status} onChange={e => set('status', e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30">
            {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
      </div>
      <div className="flex justify-end space-x-2 pt-1">
        <button type="button" onClick={onCancel} className="px-3 py-1.5 text-sm border border-gray-300 rounded text-gray-600 hover:bg-gray-100">Annuler</button>
        <button type="button" onClick={() => onSave(form)} className="px-3 py-1.5 text-sm bg-primary text-white rounded hover:bg-primary-600 font-medium">Enregistrer</button>
      </div>
    </div>
  );
};

const AccompanimentPlan = () => {
  const [searchParams] = useSearchParams();
  const entrepreneurIdParam = searchParams.get('entrepreneur');
  const planIdParam = searchParams.get('plan');

  const { entrepreneurs, plans, loading, fetchEntrepreneurs, fetchPlans, fetchPlan, createPlan, deletePlan, createAction, updateAction, deleteAction } = useCoaching();

  const [selectedEntrepreneurId, setSelectedEntrepreneurId] = useState(entrepreneurIdParam || '');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [planActions, setPlanActions] = useState([]);
  const [openAxes, setOpenAxes] = useState({});
  const [editingAction, setEditingAction] = useState(null);
  const [addingActionToAxis, setAddingActionToAxis] = useState(null);
  const [showNewPlan, setShowNewPlan] = useState(false);
  const [newPlan, setNewPlan] = useState({ title: '', start_date: '', end_date: '' });

  useEffect(() => {
    fetchEntrepreneurs();
  }, [fetchEntrepreneurs]);

  useEffect(() => {
    if (selectedEntrepreneurId) fetchPlans(selectedEntrepreneurId);
  }, [selectedEntrepreneurId, fetchPlans]);

  const loadPlan = useCallback(async (id) => {
    try {
      const plan = await fetchPlan(id);
      setSelectedPlan(plan);
      setPlanActions(plan.actions || []);
      const opened = {};
      AXES.forEach(ax => { opened[ax] = true; });
      setOpenAxes(opened);
    } catch (err) {
      toast.error('Erreur chargement plan');
    }
  }, [fetchPlan]);

  useEffect(() => {
    if (planIdParam) loadPlan(planIdParam);
  }, [planIdParam, loadPlan]);

  const actionsForAxis = (axis) => planActions.filter(a => a.axis === axis);

  const handleCreatePlan = async (e) => {
    e.preventDefault();
    if (!selectedEntrepreneurId) return toast.error('Sélectionnez un entrepreneur');
    try {
      const plan = await createPlan({ entrepreneur_id: selectedEntrepreneurId, ...newPlan });
      setShowNewPlan(false);
      setNewPlan({ title: '', start_date: '', end_date: '' });
      loadPlan(plan.id);
    } catch (err) {
      // handled in context
    }
  };

  const handleAddAction = async (axis, form) => {
    try {
      const action = await createAction(selectedPlan.id, { ...form, axis });
      setPlanActions(prev => [...prev, action]);
      setAddingActionToAxis(null);
    } catch (err) {
      toast.error('Erreur ajout action');
    }
  };

  const handleUpdateAction = async (id, form) => {
    try {
      const action = await updateAction(id, form);
      setPlanActions(prev => prev.map(a => a.id === id ? action : a));
      setEditingAction(null);
    } catch (err) {
      toast.error('Erreur modification action');
    }
  };

  const handleDeleteAction = async (id) => {
    if (!window.confirm('Supprimer cette action ?')) return;
    try {
      await deleteAction(id);
      setPlanActions(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      toast.error('Erreur suppression action');
    }
  };

  const handleDeletePlan = async () => {
    if (!window.confirm('Supprimer ce plan d\'accompagnement ? Cette action est irréversible.')) return;
    await deletePlan(selectedPlan.id);
    setSelectedPlan(null);
    setPlanActions([]);
  };

  const toggleAxis = (axis) => setOpenAxes(p => ({ ...p, [axis]: !p[axis] }));

  const downloadCSV = () => {
    if (!selectedPlan) return;
    const headers = ['Axe', 'Objectif', 'Activité', 'Res. techniques', 'Res. propres', 'Res. structure', 'Res. externes', 'Res. humaines', 'Responsable', 'Appui', 'Mois début', 'Mois fin', 'Date', 'Statut'];
    const rows = planActions.map(a => [
      a.axis, a.objective, a.activity, a.technical_resources,
      a.own_financial_resources, a.structure_financial_resources, a.external_financial_resources,
      a.human_resources, a.responsible, a.support_persons,
      a.start_month ? MONTHS[a.start_month - 1] : '',
      a.end_month ? MONTHS[a.end_month - 1] : '',
      a.implementation_date || '', STATUS_LABELS[a.status] || a.status
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${(c || '').toString().replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `plan-${selectedPlan.title.replace(/\s+/g, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const selectedEntrepreneur = entrepreneurs.find(e => e.id === selectedEntrepreneurId);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Plan d'accompagnement</h1>
          <p className="text-sm text-gray-500 mt-0.5">Construisez et suivez les plans d'action par axe stratégique</p>
        </div>
      </div>

      {/* Sélection entrepreneur */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Entrepreneur accompagné</label>
            <select
              value={selectedEntrepreneurId}
              onChange={e => { setSelectedEntrepreneurId(e.target.value); setSelectedPlan(null); setPlanActions([]); }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
            >
              <option value="">-- Sélectionner un entrepreneur --</option>
              {entrepreneurs.map(e => (
                <option key={e.id} value={e.id}>{e.company_name} — {e.entrepreneur_name}</option>
              ))}
            </select>
          </div>
          {selectedEntrepreneurId && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Plan d'accompagnement</label>
              <div className="flex space-x-2">
                <select
                  value={selectedPlan?.id || ''}
                  onChange={e => { if (e.target.value) loadPlan(e.target.value); else { setSelectedPlan(null); setPlanActions([]); } }}
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                >
                  <option value="">-- Sélectionner un plan --</option>
                  {plans.filter(p => p.entrepreneur_id === selectedEntrepreneurId).map(p => (
                    <option key={p.id} value={p.id}>{p.title} ({p.status === 'active' ? 'Actif' : p.status === 'completed' ? 'Terminé' : 'Brouillon'})</option>
                  ))}
                </select>
                <button
                  onClick={() => setShowNewPlan(true)}
                  className="px-3 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary-600 flex items-center space-x-1"
                >
                  <IconPlus /><span>Nouveau</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Infos du plan sélectionné */}
        {selectedPlan && selectedEntrepreneur && (
          <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap gap-4 text-sm">
            <div><span className="text-gray-500">Entreprise : </span><span className="font-medium">{selectedEntrepreneur.company_name}</span></div>
            <div><span className="text-gray-500">Entrepreneur : </span><span className="font-medium">{selectedEntrepreneur.entrepreneur_name}</span></div>
            {selectedPlan.start_date && <div><span className="text-gray-500">Début : </span><span className="font-medium">{new Date(selectedPlan.start_date).toLocaleDateString('fr-FR')}</span></div>}
            {selectedPlan.end_date && <div><span className="text-gray-500">Fin : </span><span className="font-medium">{new Date(selectedPlan.end_date).toLocaleDateString('fr-FR')}</span></div>}
            <div className="ml-auto flex space-x-2">
              <button onClick={downloadCSV} className="flex items-center space-x-1 px-3 py-1.5 text-xs border border-gray-300 rounded text-gray-600 hover:bg-gray-50">
                <IconDownload /><span>CSV</span>
              </button>
              <button onClick={handleDeletePlan} className="flex items-center space-x-1 px-3 py-1.5 text-xs border border-red-200 rounded text-red-600 hover:bg-red-50">
                <IconTrash /><span>Supprimer le plan</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Corps du plan — axes */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      )}

      {selectedPlan && !loading && (
        <div className="space-y-3">
          {AXES.map(axis => {
            const actions = actionsForAxis(axis);
            const isOpen = openAxes[axis];
            return (
              <div key={axis} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                {/* Axe header */}
                <button
                  onClick={() => toggleAxis(axis)}
                  className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <span className="font-semibold text-gray-900">{axis}</span>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{actions.length} action{actions.length !== 1 ? 's' : ''}</span>
                    {actions.filter(a => a.status === 'done').length > 0 && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">{actions.filter(a => a.status === 'done').length} réalisée{actions.filter(a => a.status === 'done').length > 1 ? 's' : ''}</span>
                    )}
                  </div>
                  <IconChevron open={isOpen} />
                </button>

                {/* Actions de l'axe */}
                {isOpen && (
                  <div className="border-t border-gray-100">
                    {actions.length === 0 && addingActionToAxis !== axis && (
                      <p className="text-sm text-gray-400 px-5 py-3">Aucune action définie pour cet axe.</p>
                    )}
                    {actions.map(action => (
                      <div key={action.id} className="border-b border-gray-50 last:border-0">
                        {editingAction === action.id ? (
                          <div className="p-4">
                            <ActionForm
                              initial={action}
                              fixedAxis={axis}
                              onSave={form => handleUpdateAction(action.id, form)}
                              onCancel={() => setEditingAction(null)}
                            />
                          </div>
                        ) : (
                          <div className="px-5 py-3 hover:bg-gray-50 group">
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0 space-y-1">
                                {action.objective && <p className="text-sm font-medium text-gray-900">{action.objective}</p>}
                                {action.activity && <p className="text-sm text-gray-600">{action.activity}</p>}
                                <div className="flex flex-wrap gap-2 mt-1">
                                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[action.status]}`}>
                                    {STATUS_LABELS[action.status]}
                                  </span>
                                  {action.responsible && <span className="text-xs text-gray-500">Resp. : {action.responsible}</span>}
                                  {(action.start_month || action.end_month) && (
                                    <span className="text-xs text-gray-500">
                                      {action.start_month ? MONTHS[action.start_month - 1] : '?'} – {action.end_month ? MONTHS[action.end_month - 1] : '?'}
                                    </span>
                                  )}
                                  {action.implementation_date && (
                                    <span className="text-xs text-gray-500">{new Date(action.implementation_date).toLocaleDateString('fr-FR')}</span>
                                  )}
                                  {(action.own_financial_resources > 0 || action.structure_financial_resources > 0 || action.external_financial_resources > 0) && (
                                    <span className="text-xs text-gray-500">
                                      Budget : {(
                                        (parseFloat(action.own_financial_resources) || 0) +
                                        (parseFloat(action.structure_financial_resources) || 0) +
                                        (parseFloat(action.external_financial_resources) || 0)
                                      ).toLocaleString('fr-FR')} FCFA
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex space-x-1 ml-3 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button onClick={() => setEditingAction(action.id)} className="p-1.5 text-gray-500 hover:text-primary hover:bg-gray-100 rounded"><IconEdit /></button>
                                <button onClick={() => handleDeleteAction(action.id)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"><IconTrash /></button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Formulaire ajout action */}
                    {addingActionToAxis === axis ? (
                      <div className="p-4">
                        <ActionForm
                          fixedAxis={axis}
                          onSave={form => handleAddAction(axis, form)}
                          onCancel={() => setAddingActionToAxis(null)}
                        />
                      </div>
                    ) : (
                      <div className="px-5 py-2">
                        <button
                          onClick={() => setAddingActionToAxis(axis)}
                          className="flex items-center space-x-1 text-sm text-primary hover:text-primary-700 font-medium py-1"
                        >
                          <IconPlus />
                          <span>Ajouter une action</span>
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!selectedPlan && !loading && selectedEntrepreneurId && (
        <div className="bg-white rounded-lg border border-dashed border-gray-300 p-12 text-center">
          <p className="text-gray-400 mb-3">Sélectionnez ou créez un plan d'accompagnement</p>
          <button onClick={() => setShowNewPlan(true)} className="bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-600">
            Créer un nouveau plan
          </button>
        </div>
      )}

      {!selectedEntrepreneurId && (
        <div className="bg-white rounded-lg border border-dashed border-gray-300 p-12 text-center text-gray-400">
          Sélectionnez un entrepreneur pour accéder à son plan d'accompagnement
        </div>
      )}

      {/* Modal nouveau plan */}
      {showNewPlan && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-900">Nouveau plan d'accompagnement</h3>
            </div>
            <form onSubmit={handleCreatePlan} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Intitulé du plan *</label>
                <input
                  type="text"
                  required
                  value={newPlan.title}
                  onChange={e => setNewPlan(p => ({ ...p, title: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                  placeholder="Ex : Plan d'accompagnement 2024"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date de début</label>
                  <input
                    type="date"
                    value={newPlan.start_date}
                    onChange={e => setNewPlan(p => ({ ...p, start_date: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date de fin</label>
                  <input
                    type="date"
                    value={newPlan.end_date}
                    onChange={e => setNewPlan(p => ({ ...p, end_date: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 pt-2">
                <button type="button" onClick={() => setShowNewPlan(false)} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Annuler</button>
                <button type="submit" className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary-600 font-medium">Créer le plan</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccompanimentPlan;
